export interface AspectRatio {
  width: number
  height: number
}

export interface StageBox {
  width: number
  height: number
  left: number
  top: number
  /** True when the viewport does not match the target ratio and bars are shown. */
  letterboxed: boolean
}

/**
 * A real screen is never pixel-perfect: a status bar or browser chrome can leave
 * the viewport a few pixels off 9:16. Snapping to the viewport within this margin
 * avoids a hairline black seam along one edge.
 */
const ASPECT_TOLERANCE = 0.01

export function parseAspectRatio(value: string): AspectRatio | null {
  const trimmed = value.trim().toLowerCase()
  if (trimmed === '' || /^(fill|stretch|none|off|0)$/.test(trimmed)) return null

  const match = /^(\d+(?:\.\d+)?)\s*[:/x]\s*(\d+(?:\.\d+)?)$/.exec(trimmed)
  if (!match) return null

  const width = Number.parseFloat(match[1] ?? '')
  const height = Number.parseFloat(match[2] ?? '')
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null
  return { width, height }
}

export function formatAspectRatio(aspect: AspectRatio | null): string {
  return aspect === null ? 'fill' : `${aspect.width}:${aspect.height}`
}

/**
 * Computes the largest box with the target aspect ratio that fits the viewport,
 * centred, with black bars over the remainder.
 *
 * The screens are 9:16, so composing onto a fixed 9:16 stage means a layout that
 * is correct on the screen also previews correctly on a landscape desktop, instead
 * of silently cropping the middle out of every asset.
 */
export function resolveStageBox(
  viewportWidth: number,
  viewportHeight: number,
  aspect: AspectRatio | null,
): StageBox {
  const width = Math.max(0, Math.round(viewportWidth))
  const height = Math.max(0, Math.round(viewportHeight))
  const fill: StageBox = { width, height, left: 0, top: 0, letterboxed: false }

  if (aspect === null || width === 0 || height === 0) return fill

  const target = aspect.width / aspect.height
  const viewport = width / height
  if (Math.abs(viewport - target) / target <= ASPECT_TOLERANCE) return fill

  const stage =
    viewport > target
      ? { width: Math.round(height * target), height }
      : { width, height: Math.round(width / target) }

  return {
    width: stage.width,
    height: stage.height,
    left: Math.round((width - stage.width) / 2),
    top: Math.round((height - stage.height) / 2),
    letterboxed: true,
  }
}
